import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CgvService, TenantService, UserService } from '../../core/service';

@Component({
  selector: 'app-cgv-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    CheckboxModule,
    ScrollPanelModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule
  ],
  template: `
    <p-dialog 
      [(visible)]="visible" 
      [modal]="true" 
      [closable]="false"
      [closeOnEscape]="false"
      [draggable]="false"
      [resizable]="false"
      styleClass="cgv-modal"
      [style]="{ width: '90vw', maxWidth: '1800px', height: '90vh' }">
      
      <ng-template #header>
        <div class="flex align-items-center gap-2">
          <span class="font-bold text-xl">Politique de Confidentialité</span>
        </div>
      </ng-template>

      <div class="cgv-content">
        <div class="mb-4 p-3 surface-100 border-round">
          <p class="m-0 text-700 line-height-3">
            Avant de continuer, vous devez lire et accepter notre politique de confidentialité.
            Cette acceptation est obligatoire pour utiliser notre plateforme.
          </p>
        </div>

        <p-scrollPanel [style]="{ width: '100%', height: '80%' }" styleClass="custombar1">
          <div class="cgv-text p-3" [innerHTML]="politiqueConfidentialiteContent">
          </div>

            <div class="mt-4 p-3 surface-50 border-round">
            <div class="flex align-items-start gap-3">
                <p-checkbox 
                [(ngModel)]="cgvAccepted" 
                [binary]="true" 
                inputId="cgv-checkbox"
                [disabled]="isLoading">
                </p-checkbox>
                <label for="cgv-checkbox" class="font-medium cursor-pointer flex-1">
                J'ai lu et j'accepte la politique de confidentialité
                </label>
            </div>
            </div>
        </p-scrollPanel>
      </div>

      <ng-template #footer>
        <div class="flex justify-content-between gap-2">
          <p-button 
            label="Refuser et se déconnecter" 
            icon="pi pi-times"
            (click)="refuseCgv()"
            severity="danger"
            [outlined]="true">
          </p-button>
          <p-button 
            label="Accepter et continuer" 
            icon="pi pi-check"
            [disabled]="!cgvAccepted || isLoading"
            [loading]="isLoading"
            (click)="acceptCgv()"
            severity="success">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <p-confirmDialog></p-confirmDialog>
    <p-toast key="cgv"></p-toast>
  `,
  styles: [`
    :host ::ng-deep .cgv-modal .p-dialog-header {
      color: black;
      background-color: #e7e7e7;
    }

    :host ::ng-deep .cgv-modal .p-dialog-header .p-dialog-header-icon {
      color: white;
    }

    .cgv-text {
      line-height: 1.6;
    }

    .cgv-text h3 {
      color: var(--primary-color);
      margin-top: 1.5rem;
      margin-bottom: 0.5rem;
    }

    .cgv-text p {
      margin-bottom: 1rem;
    }

    .cgv-text ul {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    .cgv-text li {
      margin-bottom: 0.25rem;
    }

    :host ::ng-deep .custombar1 .p-scrollpanel-wrapper {
      border-right: 2px solid black;
    }

    :host ::ng-deep .custombar1 .p-scrollpanel-bar {
      background-color: black;
      opacity: 1;
      transition: background-color 0.3s;
    }

    :host ::ng-deep .custombar1 .p-scrollpanel-bar:hover {
      background-color: var(--primary-600);
    }

    /* Styles pour forcer l'alignement correct de la checkbox */
    :host ::ng-deep .cgv-modal p-checkbox {
      flex-shrink: 0;
    }

    :host ::ng-deep .cgv-modal p-checkbox .p-checkbox {
      width: auto;
      height: auto;
    }

    .cursor-pointer {
      cursor: pointer;
    }
  `],
  providers: [MessageService, ConfirmationService]
})
export class CgvModalComponent implements OnInit {
  visible = false;
  cgvAccepted = false;
  isLoading = false;
  //   politiqueConfidentialiteContent = // tenant - environment.politiqueConfidentialite;

  #tenantService = inject(TenantService);
  politiqueConfidentialiteContent = this.#tenantService.getPolitiqueConfidentialite();
  #userService = inject(UserService);
  #userInfoQuery = this.#userService.getUserInfos();
  #cgvService = inject(CgvService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);

  constructor() {
    // Utiliser effect pour réagir aux changements des données
    effect(() => {
      const userData = this.#userInfoQuery.data();
      console.log('UserInfo dans CGV modal (effect)', userData);

      if (userData) {
        console.log('PolitiqueConfidentialiteAccepted:', userData.politiqueConfidentialiteAccepted);

        // Vérifier si l'utilisateur n'a pas encore accepté la politique
        if (userData.politiqueConfidentialiteAccepted === false || userData.politiqueConfidentialiteAccepted === undefined) {
          console.log('Affichage de la modal CGV car politique non acceptée');
          this.showModal();
        } else {
          console.log('Politique déjà acceptée, pas de modal à afficher');
        }
      } else {
        console.log('Données utilisateur non disponibles');
      }
    });
  }

  ngOnInit() {
    console.log('ngOnInit CGV - Composant initialisé');

    // Vérification immédiate si les données sont déjà disponibles
    const currentData = this.#userInfoQuery.data();
    console.log('Données actuelles dans ngOnInit:', currentData);
  }

  showModal() {
    this.visible = true;
    this.cgvAccepted = false;
  }

  hideModal() {
    // Cette modal ne peut pas être fermée manuellement
    // Elle ne se ferme qu'après acceptation des CGV
  }

  acceptCgv() {
    if (!this.cgvAccepted) {
      this.#messageService.add({
        key: 'cgv',
        severity: 'warn',
        summary: 'Attention',
        detail: 'Vous devez accepter les conditions générales pour continuer.'
      });
      return;
    }

    this.isLoading = true;

    this.#cgvService.acceptCgv.mutate({ accepted: true }, {
      onSuccess: () => {
        this.#messageService.add({
          key: 'cgv',
          severity: 'success',
          summary: 'Conditions acceptées',
          detail: 'Merci d\'avoir accepté nos conditions générales d\'utilisation.'
        });
        this.#userService.reloadAccessTokenInformations.mutate(undefined, {
          onSuccess: () => {
            this.visible = false;
          }
        });
      },
      onError: (error) => {
        this.#messageService.add({
          key: 'cgv',
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue lors de l\'acceptation des CGV. Veuillez réessayer.'
        });
      },
      onSettled: () => {
        this.isLoading = false;
      }
    });
  }

  refuseCgv() {
    this.#confirmationService.confirm({
      message: 'Êtes-vous sûr de vouloir refuser les conditions générales d\'utilisation ? Vous serez immédiatement déconnecté et votre compte sera supprimé sous peu.',
      header: 'Refuser les CGV - Suppression du compte',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui, supprimer mon compte',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.#cgvService.acceptCgv.mutate({ accepted: false }, {
          onSuccess: () => {
            this.#messageService.add({
              key: 'cgv',
              severity: 'warn',
              summary: 'Compte supprimé',
              detail: 'Votre compte va être supprimé sous 30 jours si vous ne changez pas d\'avis. Vous allez être déconnecté...',
              life: 2000
            });

            // Déconnecter l'utilisateur
            setTimeout(() => {
              this.#userService.logout();
            }, 2000); // Délai un peu plus long pour lire le message
          },
          onError: (error) => {
            this.#messageService.add({
              key: 'cgv',
              severity: 'error',
              summary: 'Erreur',
              detail: 'Une erreur est survenue lors de l\'acceptation des CGV. Veuillez réessayer.'
            });
          },
          onSettled: () => {
            this.isLoading = false;
          }
        });

      }
    });
  }
}
